import { useMemo, useState } from "react"
import {
	IconCheckDoubleOutline18 as CheckCheckIcon,
	IconCloudUploadOutline18 as UploadCloudIcon,
} from "nucleo-ui-outline-18"
import { parse } from "papaparse"

import { Button } from "@productlane/ui"

import { ColumnStep } from "./column-step"
import { ContactStep } from "./contact-step"
import { CreatedAtStep } from "./created-at-step"
import { HeaderStep } from "./header-step"
import { ToggleProcessedStep } from "./toggle-processed-step"
import { UploadStep } from "./upload-step"

export function CsvUploadWizard() {
	const [step, setStep] = useState(0)
	const [data, setData] = useState<string[][]>([[]])
	const [hasHeader, setHasHeader] = useState(true)

	const [textIndex, setTextIndex] = useState(0)

	const [contactEmailIndex, setContactEmailIndex] = useState<number>(-1)
	const [contactNameIndex, setContactNameIndex] = useState<number>(-1)
	const [createdAtIndex, setCreatedAtIndex] = useState<number>(-1)
	const [isProcessed, setIsProcessed] = useState<boolean>(false)

	const sortedData = useMemo(() => {
		const result = []

		if (!data[0]) {
			return [[]]
		}

		for (let i = 0; i < data[0].length; i++) {
			const newColumn = []

			for (const row of data) {
				newColumn.push(row[i] ?? "")
			}
			result.push(newColumn)
		}

		return result
	}, [data])

	const handleFileInput = (file: File) => {
		setStep(1)
		parse<string[], File>(file, {
			complete: (result) => {
				setData(result.data)
			},
			skipEmptyLines: true,
		})
	}

	const reset = () => {
		setData([[]])
		setHasHeader(true)
		setTextIndex(0)
		setStep(0)
		setContactEmailIndex(-1)
		setContactNameIndex(-1)
		setCreatedAtIndex(-1)
		setIsProcessed(false)
	}

	return (
		<div>
			{step === 0 && <UploadStep onSuccess={handleFileInput} />}
			{step === 1 && (
				<HeaderStep
					data={data}
					hasHeader={hasHeader}
					cancel={() => setStep(0)}
					select={(hasHeader) => {
						setHasHeader(hasHeader)
						setStep(2)
					}}
				/>
			)}
			{step === 2 && (
				<ContactStep
					data={data}
					hasHeaders={hasHeader}
					sortedData={sortedData}
					back={() => setStep(1)}
					next={() => setStep(3)}
					contactEmailIndex={contactEmailIndex}
					contactNameIndex={contactNameIndex}
					setContactEmailIndex={setContactEmailIndex}
					setContactNameIndex={setContactNameIndex}
				/>
			)}
			{step === 3 && (
				<CreatedAtStep
					data={data}
					hasHeaders={hasHeader}
					sortedData={sortedData}
					back={() => setStep(2)}
					next={() => setStep(4)}
					createdAtIndex={createdAtIndex}
					setCreatedAtIndex={setCreatedAtIndex}
				/>
			)}
			{step === 4 && (
				<ColumnStep
					data={data}
					hasHeaders={hasHeader}
					sortedData={sortedData}
					textIndex={textIndex}
					setTextIndex={setTextIndex}
					back={() => setStep(3)}
					next={() => setStep(5)}
				/>
			)}

			{step === 5 && (
				<ToggleProcessedStep
					isProcessed={isProcessed}
					setIsProcessed={setIsProcessed}
					contactEmailIndex={contactEmailIndex}
					contactNameIndex={contactNameIndex}
					createdAtIndex={createdAtIndex}
					hasHeaders={hasHeader}
					sortedData={sortedData}
					textIndex={textIndex}
					back={() => setStep(4)}
					onSuccess={() => setStep(6)}
				/>
			)}

			{step === 6 && (
				<div className="flex items-center justify-center p-8">
					<div className="flex flex-col items-center gap-2">
						<CheckCheckIcon className="h-8 w-8 text-green-500" />
						<div className="text-secondary">Notes successfully imported</div>
						<Button
							className="text-secondary"
							size="sm"
							variant="ghost"
							Icon={UploadCloudIcon}
							onClick={reset}
						>
							Upload another file
						</Button>
					</div>
				</div>
			)}
		</div>
	)
}
